import React, { Component, FC } from 'react';
import { IDriverSubscriptionsProps } from '../../types/propTypes/propTypes';
import { IDriverSubscriptionsGridState } from '../../types/stateTypes/stateTypes';
import DriverService from '../../services/driver/driver-service';
import { IDriverSubscription, IDriverSubscriptionList } from '../../models/driver';
import moment from 'moment';
import { Common } from '../../utils/constants';
import { Dropdown } from 'react-bootstrap';
import StandardResponse from '../../models/standard-response';
import { toast } from 'react-toastify';
import Table from '../../components/table/Table';
import { ConfirmBox } from '../../components/confirm-box/ConfirmBox';
import { IUserDetail } from '../../models/user';
import images from '../../assets/images';
import { regionDateAndTime } from '../../utils';
import { useParams } from 'react-router-dom';

const DriverTransactionGridHOC: FC<IDriverSubscriptionsProps> = (props) => {
    const match = { params: useParams() };

    return <DriverSubscriptionsGrid {...props} match={match} />;
};

class DriverSubscriptionsGrid extends Component<IDriverSubscriptionsProps, IDriverSubscriptionsGridState> {
    private driverService: DriverService;
    constructor(props: IDriverSubscriptionsProps) {
        super(props);

        this.state = {
            subscriptionList: [],
            columns: [],
            totalCount: 0,
            modal: {
                showModal: false,
                header: '',
                body: '',
                callBack: null,
            },
            driverName: '',
        };
        this.driverService = new DriverService();
    }

    async componentDidMount() {
        const { t } = this.props;

        const header = [
            {
                label: t('driverSubscriptions.label.name'),
                id: 'name',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverSubscription) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={row.name}>
                            {row.name}
                        </span>
                    );
                },
            },
            {
                label: t('driverSubscriptions.label.promotion'),
                id: 'promotion_name',
                sortable: true,
                isVisible: true,
                style: { maxWidth: 200 },
                cell: (row: IDriverSubscription) => {
                    return (
                        <span className="d-block text-truncate" style={{ maxWidth: '100%' }} title={row.promotion_name}>
                            {row.promotion_name || '-'}
                        </span>
                    );
                },
            },
            {
                label: t('driverSubscriptions.label.subscriptionType'),
                id: 'subscription_type',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverSubscription) => {
                    return (
                        <span
                            className="d-block text-nowrap-normal"
                            title={
                                row.is_subscription_paid
                                    ? row.subscription_type
                                    : t<string>('driverSubscriptions.label.freeTrial')
                            }
                        >
                            {row.is_subscription_paid
                                ? row.subscription_type
                                : t('driverSubscriptions.label.freeTrial')}
                        </span>
                    );
                },
            },
            {
                label: t('driverSubscriptions.label.subscriptionStartDate'),
                id: 'start_date',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverSubscription) => {
                    if (row.start_date) {
                        return moment(row.start_date).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT);
                    }
                    return null;
                },
            },
            {
                label: t('driverSubscriptions.label.renewalOrExpiryDate'),
                id: 'end_date',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverSubscription) => {
                    if (row.end_date) {
                        return moment(row.end_date).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT);
                    }
                    return null;
                },
            },
            {
                label: t('driverSubscriptions.label.voltsCredited'),
                id: 'premium_subscription_volts_credited',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverSubscription) => {
                    return (
                        <span
                            className="d-block text-nowrap-normal"
                            title={String(row.premium_subscription_volts_credited || '')}
                        >
                            {row.premium_subscription_volts_credited || '-'}
                        </span>
                    );
                },
            },
            {
                label: t('driverSubscriptions.label.journeysPlanned'),
                id: 'journeys_planned',
                sortable: true,
                isVisible: true,
                cell: (row: IDriverSubscription) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={String(row.journeys_planned || '')}>
                            {row.journeys_planned || '-'}
                        </span>
                    );
                },
            },
            {
                label: t('driverSubscriptions.label.subscriptionStatus'),
                id: 'active',
                sortable: true,
                isVisible: true,
                className: 'text-center',
                cell: (row: IDriverSubscription) => {
                    return (
                        <span
                            className={
                                row.active ? 'badge badge-success' : 'badge cancelled-color-code badge-secondary'
                            }
                        >
                            {row.active
                                ? t('driverSubscriptions.label.active')
                                : t('driverSubscriptions.label.cancelled')}
                        </span>
                    );
                },
            },
            {
                label: t('driverSubscriptions.label.action'),
                id: 'action',
                sortable: false,
                isVisible: true,
                style: { width: 80 },
                cell: (row: IDriverSubscription) => {
                    if (row.active) {
                        return (
                            <Dropdown className="actions">
                                <Dropdown.Toggle id="actions-dropdown">
                                    <img
                                        title={t<string>('driverSubscriptions.label.action')}
                                        className="user-icon"
                                        src={images.dropdownMenuSVG}
                                        alt=""
                                    />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {row.auto_renew && (
                                        <Dropdown.Item
                                            title={t<string>('driverSubscriptions.label.cancel')}
                                            onClick={() => {
                                                this.handleModal(
                                                    t('driverSubscriptions.message.cancelConfirmMessage'),
                                                    t('driverSubscriptions.label.cancelSubscription'),
                                                    () => this.cancelSubscription(row.id, false),
                                                );
                                            }}
                                        >
                                            <img
                                                src={images.disabledSVG}
                                                alt={t<string>('driverSubscriptions.label.cancel')}
                                            />
                                            {t('driverSubscriptions.label.cancel')}
                                        </Dropdown.Item>
                                    )}
                                    <Dropdown.Item
                                        title={t<string>('driverSubscriptions.label.terminate')}
                                        onClick={() => {
                                            const terminateMessage = row.auto_renew
                                                ? t('driverSubscriptions.message.terminateConfirmMessage')
                                                : t('driverSubscriptions.message.terminateConfirmMessageWithoutRenew');
                                            this.handleModal(
                                                terminateMessage,
                                                t('driverSubscriptions.label.terminateSubscription'),
                                                () => this.cancelSubscription(row.id, true),
                                            );
                                        }}
                                    >
                                        <img
                                            src={images.closeSVG}
                                            alt={t<string>('driverSubscriptions.label.terminate')}
                                            className="close-icon"
                                        />
                                        {t('driverSubscriptions.label.terminate')}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        );
                    }
                    return '';
                },
            },
        ];
        let driverName = '';
        let driverDetails: any = null;
        const driverID = parseInt((this.props as any).match.params.driverId);
        if (driverID && driverID > 0) {
            driverDetails = await this.getDriverDetails(driverID);
            driverName = driverDetails && driverDetails.full_name ? driverDetails.full_name : '';
        }

        this.setState(
            {
                columns: header,
                driverName,
            },
            async () => {
                if (driverDetails) {
                    const subscriptionList = await this.getDriverSubscriptionList();
                    this.setState({
                        subscriptionList,
                    });
                }
            },
        );
    }

    handleModal = (body: string, header: string, callBack: Function) => {
        this.setState({
            modal: {
                body: body,
                header: header,
                callBack: callBack,
                showModal: !this.state.modal.showModal,
            },
        });
    };

    cancelSubscription = async (driver_premium_subscription_id: number, is_terminate: boolean) => {
        const inputData = {
            is_terminate,
        };
        await this.driverService
            .cancelSubscription(driver_premium_subscription_id, inputData)
            .then(async (res: StandardResponse<number>) => {
                toast.success(res.Messages);
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
        this.setState(
            {
                ...this.state,
                modal: {
                    showModal: false,
                    header: '',
                    body: '',
                    callBack: null,
                },
            },
            async () => {
                const subscriptionList = await this.getDriverSubscriptionList();
                this.setState({
                    subscriptionList,
                });
            },
        );
    };

    getDriverSubscriptionList = async () => {
        const { rowsPerPage, offset, sortColumn, sortOrder } = this.props.filterState;
        let subscriptionList: Array<any> = [];

        const filterInput = {
            limit: rowsPerPage,
            offset: offset,
            order: [[sortColumn, sortOrder]],
        };
        const driverID = parseInt((this.props as any).match.params.driverId);
        if (driverID && driverID > 0) {
            Object.assign(filterInput, {
                driver_id: driverID,
            });

            await this.driverService
                .getDriverSubscriptionList(filterInput)
                .then(async (res: StandardResponse<IDriverSubscriptionList>) => {
                    subscriptionList = res.Data.rows;
                    this.setState({ totalCount: res.Data.count });
                })
                .catch((m) => {
                    if (m.response && m.response.data && m.response.data.message) {
                        toast.error(m.response.data.message);
                    } else {
                        toast.error(m.toString());
                    }
                });
        }

        return subscriptionList;
    };

    handlePageChange = async (currentPage: number, rowsPerPage: number) => {
        const offset = (currentPage - 1) * rowsPerPage;
        this.props.updateFilterState({ currentPage, rowsPerPage, offset }, async () => {
            const subscriptionList = await this.getDriverSubscriptionList();
            this.setState({
                subscriptionList,
            });
        });
    };

    handleSorting = async (columnName: string) => {
        const { sortColumn } = this.props.filterState;
        let sortOrder = this.props.filterState.sortOrder;
        if (sortColumn === columnName) {
            sortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        } else {
            sortOrder = 'asc';
        }

        this.props.updateFilterState({ sortOrder, sortColumn: columnName }, async () => {
            const subscriptionList = await this.getDriverSubscriptionList();
            this.setState({
                subscriptionList,
            });
        });
    };

    getDriverDetails = async (id: number) => {
        let driverDetails;
        await this.driverService
            .getDriverDetail(id)
            .then(async (res: StandardResponse<IUserDetail>) => {
                driverDetails = res.Data;
            })
            .catch((m) => {
                toast.error(m.toString());
            })
            .finally();

        return driverDetails;
    };

    render() {
        const { t } = this.props;
        const { rowsPerPage, currentPage, sortColumn, sortOrder } = this.props.filterState;

        return (
            <>
                <div className="title row align-items-content justify-content-between pb-3">
                    <div className="col-auto col-sm text-truncate pr-0">
                        <h1 className="text-truncate">
                            {this.state.driverName
                                ? `${t('driverSubscriptions.header')} - ${this.state.driverName}`
                                : t('driverSubscriptions.header')}
                        </h1>
                    </div>
                </div>

                <div className="card-outer">
                    <Table
                        columns={this.state.columns}
                        data={this.state.subscriptionList}
                        rowsPerPage={rowsPerPage}
                        currentPage={currentPage}
                        totalCount={this.state.totalCount}
                        sortColumn={sortColumn}
                        sortOrder={sortOrder}
                        handlePageChange={this.handlePageChange}
                        handleSorting={this.handleSorting}
                        t={this.props.t}
                    />
                </div>
                <ConfirmBox
                    t={this.props.t}
                    closeModel={this.handleModal}
                    header={this.state.modal.header}
                    body={this.state.modal.body}
                    callBack={this.state.modal.callBack}
                    showModel={this.state.modal.showModal}
                />
            </>
        );
    }
}

export default DriverTransactionGridHOC;
